import { Controller } from "@hotwired/stimulus"
import { i18n } from "../lib/i18n"

/**
 * Kiosk Undo Countdown Controller
 *
 * Attached to the "Undo" button on the most-recent kiosk check-in row. Shows
 * a shrinking progress ring next to the button and auto-disables it when the
 * window elapses, so staff know how long their safety net lasts.
 *
 * The server-side undo action is a normal `button_to`; this controller just
 * adds a visual countdown + disables the button after the window. If staff
 * miss the window, they can still remove the attendance from the instance
 * detail page — this is a kiosk-scoped affordance.
 *
 * Usage:
 *   <span data-controller="kiosk-undo-countdown"
 *         data-kiosk-undo-countdown-duration-value="30">
 *     <svg data-kiosk-undo-countdown-target="ring">…</svg>
 *     <span data-kiosk-undo-countdown-target="label"></span>
 *     <button data-kiosk-undo-countdown-target="button">Undo</button>
 *   </span>
 */
export default class extends Controller<HTMLElement> {
  static values = { duration: { type: Number, default: 30 } }
  static targets = ["ring", "label", "button"]

  declare readonly durationValue: number
  declare readonly hasRingTarget: boolean
  declare readonly ringTarget: SVGCircleElement
  declare readonly hasLabelTarget: boolean
  declare readonly labelTarget: HTMLElement
  declare readonly hasButtonTarget: boolean
  declare readonly buttonTarget: HTMLButtonElement

  private startedAt = 0
  private raf: number | null = null

  connect() {
    this.startedAt = performance.now()
    this.tick()
  }

  disconnect() {
    if (this.raf !== null) cancelAnimationFrame(this.raf)
    this.raf = null
  }

  private tick = () => {
    const elapsed = (performance.now() - this.startedAt) / 1000
    const remaining = Math.max(0, this.durationValue - elapsed)

    if (this.hasLabelTarget) {
      this.labelTarget.textContent = `${Math.ceil(remaining)}s`
    }

    if (this.hasRingTarget) {
      const total = Number(this.ringTarget.getAttribute("data-circumference") || "0")
      if (total > 0) {
        const pct = remaining / this.durationValue
        this.ringTarget.setAttribute("stroke-dashoffset", String(total * (1 - pct)))
      }
    }

    if (remaining <= 0) {
      this.expire()
      return
    }

    this.raf = requestAnimationFrame(this.tick)
  }

  private expire() {
    if (this.hasButtonTarget) {
      this.buttonTarget.disabled = true
      this.buttonTarget.classList.add("opacity-40", "cursor-not-allowed")
    }
    // Collapse the whole countdown container — the undo window is over.
    this.element.classList.add("opacity-0", "transition-opacity", "duration-300")
    setTimeout(() => this.element.remove(), 320)
  }
}
